import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  Icon,
  Modal,
  TextInputField,
} from "@twinkltech/curriculum-architect-component-library";
import { useTwinklAnalytics } from "@twinkltech/web-analytics-ts";
import { Button, IconActionsAdd, Text } from "@twinkltech/web-ui";
import { useId, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { BatchUpdateTableStructuresPayload } from "@/states/unitTableReducer/unitTableReducer";

import { COMMON_NS } from "@/i18n/common/common.i18n";

import { DeleteButton } from "./components/DeleteButton/DeleteButton";
import { SortableItem } from "./components/SortableItem/SortableItem";
import { PLANNER_SETTINGS_MODAL_NS } from "./PlannerSettingsModal.i18n";

export type TableHeaders = {
  id: string;
  slug: string;
  title: string;
  order: number;
};

export const PlannerSettingsModal = ({
  columnHeaders,
  onApply,
  onClose,
  rowHeaders,
  ...props
}: {
  onApply: (data: BatchUpdateTableStructuresPayload) => void;
  onClose: (open: boolean) => void;
  columnHeaders: TableHeaders[];
  rowHeaders: TableHeaders[];
} & Omit<React.ComponentProps<typeof Modal>, "children">) => {
  const { t } = useTranslation(PLANNER_SETTINGS_MODAL_NS);
  const { click } = useTwinklAnalytics();

  const [sortedRows, setSortedRows] = useState(
    rowHeaders?.sort((a, b) => a.order - b.order) || []
  );
  const [localColumns, setLocalColumns] = useState(
    columnHeaders?.sort((a, b) => a.order - b.order)
  );

  const formId = useId();

  // Set up the sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    const formEntries = Object.fromEntries(formData.entries());

    // Initialize the formatted data object
    const formattedData: BatchUpdateTableStructuresPayload = {
      rows: [],
      columns: [],
    };

    // Process row updates and deletions
    rowHeaders.forEach((originalRow) => {
      // Check if row was deleted or modified
      const formKey = `row-${originalRow.id}-title`;
      const formValue = formEntries[formKey];

      if (formValue === undefined) {
        // Row was deleted (not in form)
        formattedData.rows!.push({
          id: originalRow.id,
          toDelete: true,
        });
      } else {
        // Check if title or order changed
        const currentRowIndex =
          sortedRows.findIndex((row) => row.id === originalRow.id) + 1;

        const titleChanged = String(formValue).trim() !== originalRow.title;

        const orderChanged =
          currentRowIndex !== -1 && currentRowIndex !== originalRow.order;

        // Only add to payload if something changed
        if (titleChanged || orderChanged) {
          formattedData.rows!.push({
            id: originalRow.id,
            ...(titleChanged ? { title: String(formValue) } : {}),
            ...(orderChanged ? { order: currentRowIndex } : {}),
          });
        }
      }
    });

    // Check for new rows added in the UI but not in original data
    sortedRows.forEach((uiRow, index) => {
      // If this row doesn't exist in original headers, it's new
      if (!rowHeaders.some((original) => original.id === uiRow.id)) {
        const formKey = `row-${uiRow.id}-title`;
        const formValue = formEntries[formKey] || "";

        formattedData.rows!.push({
          id: uiRow.id,
          title: String(formValue),
          order: index,
        });
      }
    });

    // Process column updates and deletions
    columnHeaders.forEach((originalColumn) => {
      const formKey = `column-${originalColumn.id}-title`;
      const formValue = formEntries[formKey];

      // Check if column was deleted (not in form)
      if (formValue === undefined) {
        formattedData.columns!.push({
          id: originalColumn.id,
          toDelete: true,
        });
      } else {
        // Check if title changed
        const titleChanged = String(formValue).trim() !== originalColumn.title;

        // Only add to payload if something changed
        if (titleChanged) {
          formattedData.columns!.push({
            id: originalColumn.id,
            title: String(formValue),
          });
        }
      }
    });

    // Check for new columns added in the UI but not in original data
    localColumns.forEach((uiColumn) => {
      // If this column doesn't exist in original headers, it's new
      if (!columnHeaders.some((original) => original.id === uiColumn.id)) {
        const formKey = `column-${uiColumn.id}-title`;
        const formValue = formEntries[formKey] || "";

        formattedData.columns!.push({
          id: uiColumn.id,
          title: String(formValue).trim(),
        });
      }
    });

    // Remove empty arrays from payload
    if (formattedData.rows?.length === 0) delete formattedData.rows;
    if (formattedData.columns?.length === 0) delete formattedData.columns;

    onApply(formattedData);

    click("click_cta", {
      ctaCopy: "Apply",
      clickLocation: "Planner Settings Modal",
    });

    onClose(false);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setSortedRows((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);

        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const addAdditionalItem = (type: "row" | "column") => {
    // can probably be smarter about generating IDs on the fly
    const newItemId = Math.random().toString(36).substring(2, 9);
    if (type === "row") {
      setSortedRows((items) => [
        ...items,
        {
          id: newItemId,
          slug: `row-${newItemId}`,
          title: "",
          order: items.length + 1,
        },
      ]);
    } else {
      setLocalColumns((items) => [
        ...items,
        {
          id: newItemId,
          slug: `column-${newItemId}`,
          title: "",
          order: items.length + 1,
        },
      ]);
    }

    // focus the text input of the new item e.g. 'row-<defaultName>-<count>-title' or 'column-<defaultName>-<count>-title'
    setTimeout(() => {
      const newItemInput = document.querySelector(
        `input[name="${type}-${newItemId}-title"]`
      ) as HTMLInputElement;
      if (newItemInput) {
        newItemInput.focus();
        newItemInput.select();
      }
    }, 100);
  };

  const removeItem = (type: "row" | "column", id: string) => {
    if (type === "row") {
      // if it's the last row, do not allow deletion
      if (sortedRows.length <= 1) {
        return;
      }

      setSortedRows((items) => items.filter((item) => item.id !== id));
    } else {
      // if it's the last column, do not allow deletion
      if (localColumns.length <= 1) {
        return;
      }
      setLocalColumns((items) => items.filter((item) => item.id !== id));
    }
  };

  return (
    <Modal
      {...props}
      className="z-50 p-2 max-h-[95vh]"
      width="md"
      onClose={onClose}
    >
      <Modal.Header>
        <Modal.Title className="mb-6">Design Your Planner</Modal.Title>
        <Text variant="base-normal">
          Adjust the configuration of your planner. These changes will only be
          applied to this subject for the curriculum.
        </Text>
      </Modal.Header>
      <Modal.Body className="overflow-y-auto">
        <form
          className="flex flex-col gap-4"
          id={formId}
          onSubmit={handleSubmit}
        >
          <div aria-labelledby="row-settings" role="group">
            <Text
              as="h3"
              className="mb-2"
              id="row-settings"
              variant="lg-normal"
            >
              <Trans
                className="*:[span]:font-bold"
                i18nKey="row-group.title"
                ns={PLANNER_SETTINGS_MODAL_NS}
                values={{ strong: <strong /> }}
              />
            </Text>
            <DndContext
              collisionDetection={closestCenter}
              modifiers={[restrictToVerticalAxis, restrictToParentElement]}
              sensors={sensors}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={sortedRows.map((row) => row.id)}
                strategy={verticalListSortingStrategy}
              >
                {sortedRows.length === 0 ? (
                  <div className="mb-3">
                    <Text className="mb-2" variant="base-normal">
                      You currently have no rows configured.
                    </Text>
                  </div>
                ) : (
                  <ul className="flex flex-col gap-3 w-full mb-3">
                    {sortedRows?.map((row) => (
                      <SortableItem
                        key={row.id}
                        id={row.id}
                        render={({ attributes, isDragging, listeners }) => (
                          <div
                            aria-label={`Row group ${row.order + 1}`}
                            className={[
                              "flex flex-row items-center gap-2 w-full grow-1 bg-white rounded",
                              isDragging &&
                                "shadow-lg outline-2 outline-blue-500",
                            ]
                              .filter(Boolean)
                              .join(" ")}
                            role="group"
                          >
                            <Button
                              aria-label={t("row-group.drag-handle-label")}
                              className={isDragging ? "bg-gray-100" : ""}
                              type="button"
                              variant="ghost"
                              {...attributes}
                              {...listeners}
                            >
                              <Icon icon="DragHandle" />
                            </Button>
                            <TextInputField
                              className="w-full mb-0!"
                              defaultValue={row.title}
                              label={`Row ${row.order + 1}`}
                              name={`row-${row.id}-title`}
                            />
                            <DeleteButton
                              disabled={sortedRows.length <= 1}
                              onDelete={() => removeItem("row", row.id)}
                            />
                          </div>
                        )}
                      />
                    ))}
                  </ul>
                )}
                <div className="flex justify-end">
                  <Button
                    className="mr-[58px]"
                    size="sm"
                    type="button"
                    variant="outlined"
                    onClick={() => addAdditionalItem("row")}
                  >
                    <IconActionsAdd className="mr-2" />
                    {t("row-group.add-action")}
                  </Button>
                </div>
              </SortableContext>
            </DndContext>
          </div>
          <div aria-labelledby="column-settings" role="group">
            <Text
              as="h3"
              className="mb-2"
              id="column-settings"
              variant="lg-normal"
            >
              <Trans
                className="*:[span]:font-bold"
                i18nKey="column-group.title"
                ns={PLANNER_SETTINGS_MODAL_NS}
                values={{ strong: <strong /> }}
              />
            </Text>
            {localColumns.length === 0 ? (
              <div className="mb-3">
                <Text className="mb-2" variant="base-normal">
                  You currently have no columns configured.
                </Text>
              </div>
            ) : (
              <ul className="flex flex-col gap-3 w-full mb-3">
                {localColumns?.map((col) => (
                  <li
                    key={col.id}
                    aria-label={`Column item ${col.order + 1}`}
                    className="flex flex-row items-center gap-2 w-full"
                  >
                    <div className="flex flex-row items-center gap-2 w-full grow-1">
                      <TextInputField
                        className="w-full mb-0!"
                        defaultValue={col.title}
                        label={`Column ${col.order + 1}`}
                        name={`column-${col.id}-title`}
                      />

                      <DeleteButton
                        disabled={localColumns.length <= 1}
                        onDelete={() => removeItem("column", col.id)}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex justify-end">
              <Button
                className="mr-[58px]"
                size="sm"
                type="button"
                variant="outlined"
                onClick={() => addAdditionalItem("column")}
              >
                <IconActionsAdd className="mr-2" />
                {t("column-group.add-action")}
              </Button>
            </div>
          </div>
        </form>
      </Modal.Body>
      <Modal.Footer className="flex justify-end gap-2">
        <Button
          variant="outlined"
          onClick={() => {
            click("click_cta", {
              ctaCopy: "Cancel",
              clickLocation: "Planner Settings Modal",
            });
            onClose(false);
          }}
        >
          {t("actions.cancel", {
            ns: COMMON_NS,
          })}
        </Button>
        <Button form={formId} type="submit" variant="primary">
          {t("cta.apply")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
